import React from "react";
import moment from "moment";

const OrderList = ({
  orderDetails,
  handleStatusChange,
  defaultSettings,
  isFetching,
}) => {
  /* status dropdown color mapping */
  const statusColors = {
    pending: "bg-yellow-100 text-yellow-600",
    accepted: "bg-green-100 text-green-500",
    preparing: "bg-orange-100 text-orange-500",
    delivering: "bg-blue-100 text-blue-500",
    delivered: "bg-teal-100 text-teal-500",
    canceled: "bg-red-200 text-red-500",
  };

  return (
    <>
      <tbody>
        {orderDetails?.map((order) =>
          order.order_details?.map((detail) => {
            // Calculate if the order can be cancelled or confirmed based on the timespan
            const orderCreatedTime = moment(order.created_at);
            const currentTime = moment();
            const timeSinceCreation = moment.duration(
              currentTime.diff(orderCreatedTime)
            );

            const canCancel =
              timeSinceCreation.asMinutes() <=
              defaultSettings?.cancellation_time_span;

            const canConfirm =
              timeSinceCreation.asMinutes() <=
              defaultSettings?.confirmation_time_span;

            return (
              <tr className="border-b" key={detail.id}>
                <td>
                  <p className="text-primaryGreen text-[14px] mb-0">
                    {order.order_code}
                  </p>
                </td>

                <td>
                  <h4 className="text-[14px] mb-0 leading-tight">
                    {detail.name}
                  </h4>
                </td>

                {/* Order Date & Time */}
                <td>
                  <h4 className="text-[14px] mb-1 leading-tight">
                    - {new Date(order.created_at).toLocaleDateString()}
                  </h4>

                  <h4 className="text-[13px] mb-1 leading-tight">
                    -{" "}
                    {new Date(order.created_at).toLocaleDateString("en-US", {
                      weekday: "long",
                    })}
                  </h4>

                  <h4 className="text-[14px] mb-1 leading-tight">
                    - {new Date(order.created_at).toLocaleTimeString()}
                  </h4>
                </td>

                {/* Delivery Date & Time Slot */}
                <td>
                  <h4 className="text-[14px] mb-1 leading-tight">
                    -{" "}
                    {order.delivery_time
                      ? moment(
                          order.delivery_time,
                          "YYYY-MM-DD HH:mm:ss"
                        ).format("MM/DD/YYYY")
                      : "N/A"}
                  </h4>

                  <h4 className="text-[13px] mb-1 leading-tight">
                    -{" "}
                    {order.delivery_time
                      ? moment(
                          order.delivery_time,
                          "YYYY-MM-DD HH:mm:ss"
                        ).format("dddd")
                      : "N/A"}
                  </h4>

                  <h4 className="text-[14px] mb-1 leading-tight">
                    - {order.delivery_slot || "N/A"}
                  </h4>
                </td>

                {/* Delivery address */}
                <td>
                  <h4 className="text-[12px] mb-1 leading-tight">
                    -{" "}
                    {(order?.order_delivery_address?.address_type === "home" &&
                      order?.order_delivery_address?.home_street_address &&
                      order?.order_delivery_address?.home_street_address) ||
                      (order?.order_delivery_address?.address_type ===
                        "office" &&
                        order?.order_delivery_address?.office_street_address &&
                        `${order?.order_delivery_address?.office_street_address}, ${order?.order_delivery_address?.office_building_no} ${order?.order_delivery_address?.office_floor}, ${order?.order_delivery_address?.office_company}`) ||
                      (order?.order_delivery_address?.address_type ===
                        "apartment" &&
                        order?.order_delivery_address
                          ?.apartment_street_address &&
                        `${order?.order_delivery_address?.apartment_street_address}, ${order?.order_delivery_address?.apartment_name}, ${order?.order_delivery_address?.apartment_apartment_no}, ${order?.order_delivery_address?.apartment_floor}`) ||
                      "not available"}
                  </h4>

                  <h4 className="text-[12px] mb-1 leading-tight">
                    {(order?.order_delivery_address?.address_type === "home" &&
                      order?.order_delivery_address?.home_city &&
                      `- ${order?.order_delivery_address?.home_city}`) ||
                      (order?.order_delivery_address?.address_type ===
                        "office" &&
                        order?.order_delivery_address?.office_city &&
                        `- ${order?.order_delivery_address?.office_city}`) ||
                      (order?.order_delivery_address?.address_type ===
                        "apartment" &&
                        order?.order_delivery_address?.apartment_city &&
                        `- ${order?.order_delivery_address?.apartment_city}`) ||
                      ""}
                  </h4>
                </td>

                {/* Customer */}
                <td>
                  <h4 className="text-[12px] mb-0 leading-tight">
                    {order.name}
                  </h4>
                </td>

                {/* Phone */}
                <td>
                  <h4 className="text-[12px] mb-0 leading-tight">
                    {order.phone}
                  </h4>
                </td>

                {/* Quantity */}
                <td>
                  <h4 className="text-[14px] mb-0 leading-tight">
                    x{detail.quantity}
                  </h4>
                </td>

                {/* Chef Earning */}
                <td>
                  <h4 className="text-[14px] mb-0 leading-tight">
                    {order.chef_earning_price.toLocaleString("en-PK", {
                      style: "currency",
                      currency: "PKR",
                    })}
                  </h4>
                </td>

                {/* Total Price */}
                <td>
                  <h4 className="text-[14px] mb-0 leading-tight">
                    {order.total_price.toLocaleString("en-PK", {
                      style: "currency",
                      currency: "PKR",
                    })}
                  </h4>
                </td>

                {/* Action */}
                <td>
                  <select
                    className={`text-sm font-medium w-max ${
                      statusColors[order.status]
                    }`}
                    onChange={(e) => handleStatusChange(e, order.id)}
                    defaultValue={order.status}
                  >
                    <option value="pending" hidden={true}>
                      Pending
                    </option>

                    <option
                      value="accepted"
                      hidden={order.status !== "pending" || !canConfirm}
                    >
                      Accepted
                    </option>

                    <option
                      value="preparing"
                      hidden={order.status !== "accepted"}
                    >
                      In Process
                    </option>

                    <option
                      value="delivering"
                      hidden={order.status !== "preparing"}
                    >
                      Ready to Deliver
                    </option>

                    <option
                      value="delivered"
                      hidden={order.status !== "delivering"}
                    >
                      Delivered
                    </option>

                    <option
                      className="text-primary"
                      value="canceled"
                      hidden={
                        order.status === "accepted" ||
                        order.status === "delivered" ||
                        order.status === "delivering" ||
                        order.status === "preparing" ||
                        !canCancel
                      }
                    >
                      Cancelled
                    </option>
                  </select>
                </td>
              </tr>
            );
          })
        )}

        {orderDetails?.length < 1 && !isFetching && (
          <tr>
            <td colSpan="5" className="font-semibold text-headGray">
              No Order Found
            </td>
          </tr>
        )}

        {isFetching && (
          <tr>
            <td colSpan="8" className="text-center font-semibold text-headGray">
              Fetching Orders...
            </td>
          </tr>
        )}
      </tbody>
    </>
  );
};

export default OrderList;

